import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { AlertCircle, AlertTriangle, Braces, Check, ListTree, Loader2, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { FieldFormula, FormulaIssue, VariableOption } from "@/types/formula";
import { FORMULA_OPERATORS, fillHole, holeAt, holeIsPath, hyphenatedNames, insertFunction, insertHole, insertOperator, issueSpan, markSource, pathFragmentAt, replaceFragment, templateHoleSources } from "@/utils/formula";
import { useFormulaCheck } from "@/hooks/use-formula-check";
import { FunctionPalette } from "./function-palette";
import { VariableSuggestions } from "./variable-suggestions";

/**
 * Typography shared by the input and the layer that underlines it.
 *
 * The two have to wrap identically or a mark lands under the wrong word, and the only way
 * to guarantee that is one declaration used twice — a font size copied into two class lists
 * drifts the first time one of them is adjusted.
 */
const SOURCE_TYPOGRAPHY = "whitespace-pre-wrap break-words px-2 py-1 font-mono text-[12px] leading-5";

/** `leading-5`: the list opens one line below the caret's top. */
const CARET_LINE_HEIGHT = 20;

/** Wide enough for a path and its type badge; a narrower box keeps the list at its own width. */
const SUGGESTIONS_WIDTH = 320;

/**
 * The quiet square buttons beside a compact box, sized to its first line.
 *
 * Ghost, like the row actions of the list filters: in a column of short values the icons
 * are a way out, not the content, and a bordered button per icon reads as three more inputs.
 */
const SIDE_BUTTON = "flex h-[30px] w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700";

type FormulaEditorProps = {
    id: string;
    value: string;
    formula: FieldFormula;
    /** The catalogue, already fetched by the field so a dozen inputs share one request. */
    options: VariableOption[];
    loadingOptions?: boolean;
    disabled?: boolean;
    rows?: number;
    /**
     * The source box and its diagnostics, without the workshop around them.
     *
     * What a converted field gets in the panel: an operator row, thirty functions and the
     * writing rule above a field that mostly holds `'Relance dossier'` is not help, it is
     * the field pushed out of a 400px column. The whole environment is one click away —
     * `onExpand` — and the check still travels with the value, which is what the author
     * needs in place: red under the characters, and the sentence the engine wrote.
     */
    compact?: boolean;
    /**
     * Says nothing while there is nothing to say: no « every variable resolves », no
     * « empty for now ». For a list of short values, where one status line per row would
     * double the height of the list; a problem, or a check in flight, is still shown.
     */
    quiet?: boolean;
    /**
     * There is room: the vocabulary opens instead of waiting to be asked for.
     *
     * The palette is collapsed in the panel for want of space, which is a compromise and
     * not a preference — thirty functions is the answer to « what can I even write here »,
     * and a modal has the room to answer it without being asked.
     */
    roomy?: boolean;
    /** Opens the roomy editor. Rendered as a button beside the source when given. */
    onExpand?: () => void;
    /**
     * A panel beside the source, handed the insertion the editor knows how to make.
     *
     * How the modal puts a permanent list of variables next to a large box without the
     * caret arithmetic leaving this file.
     */
    aside?: (insert: (path: string) => void) => ReactNode;
    onChange: (value: string) => void;
    /** Offered when the source is a plain path again, to go back to the simple picker. */
    onSimplify?: () => void;
};

/**
 * The expression input: what was typed, what is wrong with it, and where.
 *
 * It underlines rather than refuses. The drawer writes on every keystroke on purpose — a step
 * is created before it is configured and lives half filled for as long as its author needs —
 * so a formula being typed is always momentarily wrong, and an input that withheld the write
 * until the source parsed would be an input nobody can use. Diagnostics therefore travel
 * beside the value: a wavy line at the offset the engine reported, the sentence it wrote
 * underneath, and the value saved either way. Publication is where a wrong formula is stopped,
 * and it already is, server-side.
 *
 * Nothing here knows what the field configures. The mode says whether this is one expression
 * or text with holes, and that is the only branch in the file.
 */
export function FormulaEditor({ id, value, formula, options, loadingOptions, disabled, rows, compact, quiet, roomy, onExpand, aside, onChange, onSimplify }: FormulaEditorProps) {
    const input = useRef<HTMLTextAreaElement>(null);
    const [caret, setCaret] = useState(value.length);
    const [focused, setFocused] = useState(false);
    const [browsing, setBrowsing] = useState(false);
    /** Source the list was dismissed on, so picking a variable does not reopen it on itself. */
    const [dismissed, setDismissed] = useState<string | null>(null);
    /** Set when an insertion moved the caret, and applied after the value has been rendered. */
    const pendingCaret = useRef<number | null>(null);

    const template = formula.mode === "template";
    const check = useFormulaCheck(value, formula);
    const issues = check.checked === value ? (check.result?.issues ?? []) : [];
    const errors = issues.filter(issue => issue.severity === "error");
    const warnings = issues.filter(issue => issue.severity !== "error");

    const hole = template ? holeAt(value, caret) : null;
    const fragment = pathFragmentAt(value, caret);
    // Inside a hole, the name under the caret is what is completed, so a variable is
    // suggested in `{{ concat('Hello ', snap` exactly as in `{{ snap`.
    const query = template ? (hole ? fragment.query : "") : fragment.query;
    const suggesting = focused && !disabled && dismissed !== value && (template ? hole !== null : browsing || query.length > 0);

    // A compact box grows with what it holds instead of scrolling inside itself: a value
    // clipped to its first line, `{{` and nothing after, is a value nobody can review.
    useLayoutEffect(() => {
        const element = input.current;
        if (!compact || !element) return;
        const fit = () => {
            element.style.height = "auto";
            // Nothing measured — the box is not laid out yet — leaves the rows to decide.
            if (element.scrollHeight === 0) element.style.height = "";
            else element.style.height = `${element.scrollHeight + element.offsetHeight - element.clientHeight}px`;
        };
        fit();
        // A narrower column wraps the same value onto more lines.
        if (typeof ResizeObserver === "undefined") return;
        let width = element.clientWidth;
        const observer = new ResizeObserver(() => {
            if (element.clientWidth === width) return;
            width = element.clientWidth;
            fit();
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [compact, value]);

    useLayoutEffect(() => {
        const target = pendingCaret.current;
        if (target === null || !input.current) return;
        pendingCaret.current = null;
        input.current.focus();
        input.current.setSelectionRange(target, target);
        setCaret(target);
    }, [value]);

    const write = ({ source, caret: next }: { source: string; caret: number }) => {
        pendingCaret.current = next;
        setDismissed(source);
        setBrowsing(false);
        onChange(source);
    };

    const pick = (path: string, continues: boolean) => {
        if (template && hole && holeIsPath(value, hole, caret)) {
            write(fillHole(value, hole, path, { open: continues }));
            if (continues) setDismissed(null);
            return;
        }
        write(replaceFragment(value, caret, path, { open: continues }));
        if (continues) setDismissed(null);
    };

    const readCaret = (element: HTMLTextAreaElement) => setCaret(element.selectionStart ?? element.value.length);

    /**
     * Where the caret sits inside the box, so the list opens under the word being completed
     * rather than under the whole box — in a six-line message the box's bottom edge can be
     * far from where the author is typing. Read from a mirror wearing the input's metrics,
     * the same trick the marks layer plays: only the browser knows where a line wraps.
     */
    const probe = useRef<HTMLSpanElement>(null);
    const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
    useLayoutEffect(() => {
        if (!suggesting || !probe.current || !input.current) return;
        const box = input.current;
        setAnchor({ top: probe.current.offsetTop - box.scrollTop + CARET_LINE_HEIGHT, left: probe.current.offsetLeft, width: box.clientWidth });
    }, [suggesting, value, caret]);
    const listStyle =
        anchor && anchor.width > SUGGESTIONS_WIDTH
            ? { top: anchor.top + 2, left: Math.max(0, Math.min(anchor.left, anchor.width - SUGGESTIONS_WIDTH)), width: SUGGESTIONS_WIDTH }
            : undefined;

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-start gap-1">
            <div className="relative min-w-0 flex-1">
                {/* Behind the input, wearing its metrics and none of its interactivity: the marks
                    follow the source's own wrapping, which no measuring of characters could. */}
                <div aria-hidden className={cn("pointer-events-none absolute inset-0 overflow-hidden text-transparent", SOURCE_TYPOGRAPHY)} data-testid={`formula-marks-${id}`}>
                    {markSource(value, issues).map((segment, index) => (
                        <span
                            key={index}
                            data-severity={segment.severity ?? undefined}
                            className={cn(
                                segment.severity === "error" && "underline decoration-red-500 decoration-wavy decoration-2 underline-offset-2",
                                segment.severity === "warning" && "underline decoration-amber-500 decoration-wavy decoration-2 underline-offset-2"
                            )}
                        >
                            {segment.text}
                        </span>
                    ))}
                </div>
                <textarea
                    ref={input}
                    id={id}
                    name={id}
                    value={value}
                    rows={rows ?? (template ? 4 : 2)}
                    disabled={disabled}
                    spellCheck={false}
                    aria-invalid={errors.length > 0}
                    aria-describedby={`${id}-formula-status`}
                    className={cn(
                        "relative w-full rounded border border-input bg-transparent",
                        compact ? "resize-none overflow-hidden rounded-sm bg-white shadow-sm" : "resize-y",
                        SOURCE_TYPOGRAPHY,
                        errors.length > 0 && "border-red-400"
                    )}
                    placeholder={template ? "Text with variables between {{ … }}" : "A field, or an expression such as coalesce(snapshot.mobile_phone, snapshot.phone)"}
                    onChange={event => {
                        setDismissed(null);
                        readCaret(event.target);
                        onChange(event.target.value);
                    }}
                    onClick={event => readCaret(event.currentTarget)}
                    onKeyUp={event => readCaret(event.currentTarget)}
                    onKeyDown={event => {
                        if (event.key === "Escape" && suggesting) {
                            event.preventDefault();
                            setDismissed(value);
                            setBrowsing(false);
                        }
                    }}
                    onFocus={event => {
                        setFocused(true);
                        readCaret(event.currentTarget);
                    }}
                    onBlur={() => {
                        setFocused(false);
                        setBrowsing(false);
                    }}
                />
                <div aria-hidden className={cn("pointer-events-none invisible absolute inset-0 overflow-hidden border border-transparent", SOURCE_TYPOGRAPHY)}>
                    {value.slice(0, caret)}
                    <span ref={probe}>\u200b</span>
                </div>
                {suggesting && (
                    <VariableSuggestions
                        options={options}
                        query={query}
                        loading={loadingOptions}
                        onPick={pick}
                        style={listStyle}
                        className={cn("absolute z-30", listStyle ? "" : "left-0 right-0 top-full mt-1")}
                    />
                )}
                </div>
                {compact && template && !disabled && (
                    /* The one affordance a one-line template cannot do without: an author
                       who does not know the `{{` convention still finds the variables. */
                    <button
                        type="button"
                        aria-label="Insert a variable"
                        title="Insert a variable"
                        data-testid={`formula-browse-${id}`}
                        onMouseDown={event => {
                            event.preventDefault();
                            input.current?.focus();
                            write(insertHole(value, caret));
                            // After the write, which dismisses the list on what it wrote:
                            // an empty hole is precisely where the list is wanted.
                            setDismissed(null);
                        }}
                        className={SIDE_BUTTON}
                    >
                        <Braces className="h-3.5 w-3.5" />
                    </button>
                )}
                {onExpand && (
                    <button
                        type="button"
                        aria-label={template ? "Open the editor" : "Open the expression editor"}
                        title={
                            template
                                ? "Open a larger editor, with the variables listed beside the text."
                                : "Open the expression editor: the variables, the functions and the room to read what you wrote."
                        }
                        data-testid={`formula-expand-${id}`}
                        onClick={onExpand}
                        className={compact ? SIDE_BUTTON : "flex h-9 shrink-0 items-center rounded border px-1.5 text-gray-600 hover:bg-gray-50"}
                    >
                        <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                )}
                {aside?.(path => pick(path, false))}
            </div>

            {!compact && !disabled && (
                <div className="flex flex-wrap items-center gap-1">
                    <button
                        type="button"
                        data-testid={`formula-browse-${id}`}
                        onMouseDown={event => {
                            event.preventDefault();
                            input.current?.focus();
                            if (template) write(insertHole(value, caret));
                            // After the write, which dismisses the list on what it wrote.
                            setDismissed(null);
                            setBrowsing(true);
                        }}
                        className="flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                    >
                        {template ? <Braces className="h-3 w-3" /> : <ListTree className="h-3 w-3" />}
                        {template ? "Insert a variable" : "Variables"}
                    </button>

                    {!template &&
                        FORMULA_OPERATORS.map(operator => (
                            <button
                                key={operator}
                                type="button"
                                aria-label={`Insert the ${operator} operator`}
                                data-testid={`formula-operator-${operator}`}
                                /* The spaces come with the operator, which is the whole point of
                                   offering it as a button: `attempts.relance-1` is a step name and
                                   `attempts.relance - 1` is a subtraction. */
                                onMouseDown={event => {
                                    event.preventDefault();
                                    write(insertOperator(value, caret, operator));
                                }}
                                className="rounded border px-1.5 py-0.5 font-mono text-[11px] text-gray-700 hover:bg-gray-50"
                            >
                                {operator}
                            </button>
                        ))}
                </div>
            )}

            {!compact && !disabled && <FunctionPalette open={roomy} onPick={name => write(insertFunction(value, caret, name))} />}

            {/* In a template the rule is about the holes only, and said only when one of them
                actually holds a hyphenated name: under a subject line the general rule is a
                sentence about arithmetic nobody there is writing. */}
            {!compact && <SpacingRule source={template ? templateHoleSources(value) : value} general={!template} />}

            {(!quiet || check.checking || check.unavailable || issues.length > 0) && (
            <p id={`${id}-formula-status`} className="flex items-center gap-1 text-[11px]" data-testid={`formula-status-${id}`}>
                {check.checking ? (
                    <span className="flex items-center gap-1 text-gray-500">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Checking…
                    </span>
                ) : check.unavailable ? (
                    <span className="text-gray-500">This formula could not be checked from here; publication will check it.</span>
                ) : errors.length > 0 ? (
                    <span className="text-red-600">
                        {errors.length} problem{errors.length > 1 ? "s" : ""} that would stop a publication
                    </span>
                ) : warnings.length > 0 ? (
                    <span className="text-amber-700">
                        {warnings.length} warning{warnings.length > 1 ? "s" : ""}, which do not stop a publication
                    </span>
                ) : value.trim() ? (
                    <span className="flex items-center gap-1 text-emerald-700">
                        <Check className="h-3 w-3" />
                        {template ? "Every variable resolves" : `Reads as ${check.result?.type ?? "a value"}`}
                    </span>
                ) : (
                    <span className="text-gray-500">Empty for now, which is saved and refused only at publication.</span>
                )}
            </p>
            )}

            {issues.length > 0 && (
                <ul className="space-y-0.5" data-testid={`formula-issues-${id}`}>
                    {/* One line in place, all of them in the modal: a converted field is
                        one box in a column of forty, and four sentences under it push the
                        rest of the form out of view. The count is in the status above. */}
                    {(compact ? issues.slice(0, 1) : issues).map((issue, index) => (
                        <li key={`${issue.code}-${issue.position}-${index}`}>
                            <IssueLine
                                issue={issue}
                                onLocate={() => {
                                    const span = issueSpan(value, issue);
                                    input.current?.focus();
                                    input.current?.setSelectionRange(span.start, span.end);
                                    setCaret(span.start);
                                }}
                            />
                        </li>
                    ))}
                </ul>
            )}

            {onSimplify && !disabled && (
                <button type="button" onClick={onSimplify} data-testid={`formula-simplify-${id}`} className="self-start text-[11px] text-sky-700 underline hover:text-sky-900">
                    This is just a field: pick it from the list instead
                </button>
            )}
        </div>
    );
}

/**
 * One diagnostic, in the words the engine wrote and clickable onto the characters it is
 * about.
 *
 * The sentence comes from the server and is never rephrased here: the engine knows why
 * `snapshot.mobil_phone` is refused and this component does not. Clicking selects the span
 * the offset points at, which is what turns « position 12 » into something an author can act
 * on without counting characters.
 */
function IssueLine({ issue, onLocate }: { issue: FormulaIssue; onLocate: () => void }) {
    const error = issue.severity === "error";
    return (
        <button
            type="button"
            onClick={onLocate}
            data-testid={`formula-issue-${issue.code}`}
            className={cn("flex w-full items-start gap-1 rounded px-1 py-0.5 text-left text-[11px] leading-snug hover:bg-gray-50", error ? "text-red-700" : "text-amber-800")}
        >
            {error ? <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />}
            <span>{issue.message}</span>
        </button>
    );
}

/**
 * The one writing rule of this language, where an author can trip on it.
 *
 * A space is what asks for arithmetic. Every retry counter in the existing graphs has a
 * hyphenated step id, so `attempts.relance-1` is a *name* and `attempts.relance - 1` is a
 * subtraction — and the difference is invisible unless somebody says it. Stated under every
 * expression input rather than hidden in a hover, and stated *about the source in front of
 * the author* the moment it actually holds a hyphenated name, because a general rule is read
 * once and a rule about what one just typed is read every time.
 */
function SpacingRule({ source, general }: { source: string; general: boolean }) {
    const hyphenated = hyphenatedNames(source);

    if (hyphenated.length === 0) {
        if (!general) return null;
        return (
            <p className="text-[11px] leading-snug text-gray-500" data-testid="spacing-rule">
                A space is what asks for arithmetic: <span className="font-mono">attempts.relance-1</span> is one name, <span className="font-mono">attempts.relance - 1</span> is a subtraction.
            </p>
        );
    }

    return (
        <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] leading-snug text-amber-900" data-testid="spacing-rule-hit">
            {hyphenated.map(name => (
                <span key={name} className="font-mono">
                    {name}{" "}
                </span>
            ))}
            {hyphenated.length > 1 ? "are read as names" : "is read as one name"}, hyphen included — which is right for a step id. Put spaces around the sign to subtract:{" "}
            <span className="font-mono">{hyphenated[0].replace(/-([^-]*)$/, " - $1")}</span>.
        </p>
    );
}
